import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../components/LanguageContext';
import Nav from '../components/Nav';
import Form from '../components/FormBlock';
import Footer from '../components/Footer';

const WorkPage = () => {
  const { language, translations } = useLanguage();
  const lineRef = useRef(null);
  const containerRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (!lineRef.current || !containerRef.current) return;
      const container = containerRef.current;
      const line = lineRef.current;
      const containerHeight = container.offsetHeight;
      const scrollTop = window.scrollY;
      let progress = (scrollTop * 1.4) / containerHeight;
      progress = Math.max(0, Math.min(1, progress));
      line.style.background = `linear-gradient(to bottom, black ${progress * 100}%, rgba(0, 0, 0, 0.3) ${progress * 100}%)`;
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const work = translations['workTranslations.json']?.work || {};

  const renderTextWithStyles = (text) => {
    if (!text) return null;
    const keywords = ['Education', 'Experience', 'Languages', 'Location'];
    return text.split('\n').map((line, index, array) => {
      const parts = line.split(new RegExp(`(${keywords.join('|')})`, 'g')).map((part, i) => {
        if (keywords.includes(part)) {
          return <span key={i} style={{ fontWeight: 700 }}>{part}</span>;
        }
        return part;
      });
      return (
        <React.Fragment key={index}>
          {parts}
          {index < array.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="content_container">
      <Nav />
      <div className="container" ref={containerRef}>
        <h1 className="work_header-title">{work.title?.[language]}</h1>
        <p className="work_descr-title">{renderTextWithStyles(work.header_description?.[language])}</p>
        <div className="work_container">
          <div className="title_work-block">
            <div className="title_block">
              <span>01</span>
              <p>{renderTextWithStyles(work.stages?.finding_the_right_tender?.title?.[language])}</p>
            </div>
            <div className="work_descr-item">
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.finding_the_right_tender?.items?.tender_search?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.finding_the_right_tender?.items?.tender_search?.description?.[language])}</p>
              </div>
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.finding_the_right_tender?.items?.feasibility_assessment?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.finding_the_right_tender?.items?.feasibility_assessment?.description?.[language])}</p>
              </div>
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.finding_the_right_tender?.items?.market_research?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.finding_the_right_tender?.items?.market_research?.description?.[language])}</p>
              </div>
            </div>
          </div>
          <div className="title_work-block">
            <div className="title_block">
              <span>02</span>
              <p>{renderTextWithStyles(work.stages?.pre_tender_preparation?.title?.[language])}</p>
            </div>
            <div className="work_descr-item">
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.pre_tender_preparation?.items?.clarifications?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.pre_tender_preparation?.items?.clarifications?.description?.[language])}</p>
              </div>
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.pre_tender_preparation?.items?.bid_preparation?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.pre_tender_preparation?.items?.bid_preparation?.description?.[language])}</p>
              </div>
            </div>
          </div>
          <div className="title_work-block">
            <div className="title_block">
              <span>03</span>
              <p>{renderTextWithStyles(work.stages?.tendering_stage?.title?.[language])}</p>
            </div>
            <div className="work_descr-item">
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.tendering_stage?.items?.bid_submission?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.tendering_stage?.items?.bid_submission?.description?.[language])}</p>
              </div>
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.tendering_stage?.items?.auction_participation?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.tendering_stage?.items?.auction_participation?.description?.[language])}</p>
              </div>
            </div>
          </div>
          <div className="title_work-block">
            <div className="title_block">
              <span>04</span>
              <p>{renderTextWithStyles(work.stages?.contractual_phase?.title?.[language])}</p>
            </div>
            <div className="work_descr-item">
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.contractual_phase?.items?.submit_required_documents?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.contractual_phase?.items?.submit_required_documents?.description?.[language])}</p>
              </div>
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.contractual_phase?.items?.contract_finalization?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.contractual_phase?.items?.contract_finalization?.description?.[language])}</p>
              </div>
            </div>
          </div>
          <div className="title_work-block">
            <div className="title_block">
              <span>05</span>
              <p>{renderTextWithStyles(work.stages?.post_tender_engagement?.title?.[language])}</p>
            </div>
            <div className="work_descr-item">
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.post_tender_engagement?.items?.contract_execution?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.post_tender_engagement?.items?.contract_execution?.description?.[language])}</p>
              </div>
              <div className="work_descr">
                <p className="work-descr_title">{work.stages?.post_tender_engagement?.items?.future_opportunities?.title?.[language]}</p>
                <p className="work_descr-descr">{renderTextWithStyles(work.stages?.post_tender_engagement?.items?.future_opportunities?.description?.[language])}</p>
              </div>
            </div>
          </div>
          <div className="line_item" ref={lineRef}></div>
        </div>
        <Form />
        <Footer />
      </div>
    </div>
  );
};

export default WorkPage;